import { computed, Directive, inject, Signal, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '@unity/services/navigation.service';
import { NewFormService } from '@unity/services/new-form.service';
import { ToasterService } from '@unity/services/toaster.service';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';
import { Observable } from 'rxjs';

@Directive() // See https://github.com/angular/angular/issues/37769
export abstract class AbstractNewComponent<
  // Needed for interop
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TCreateDto extends Record<string, any>,
  TSchema extends FormGroupControlSchema<TCreateDto>,
  TResponseDto,
> {
  private readonly formBuilder = inject(FormBuilder);
  private readonly newFormService = inject(NewFormService);
  private readonly toasterService = inject(ToasterService);
  private readonly navigationService = inject(NavigationService);

  protected abstract createResourceFn$: (form: FormGroup<TSchema>) => Observable<TResponseDto>;
  /** Creates a form schema, which can contain reactive values. The $formGroup will be reactive to any signals invoked within this function. */
  protected abstract newControlsFn(): TSchema;
  protected abstract redirectUrlFn: (response: TResponseDto) => UnityRouteUrlTrees;
  protected abstract readonly $contentHeader: Signal<string>;

  protected readonly $formGroup = computed(() => {
    const controls = this.newControlsFn.bind(this)();
    return this.formBuilder.group<TSchema>(controls);
  });

  protected readonly $formLoading = signal(false);

  protected onSubmit() {
    this.$formLoading.set(true);

    this.newFormService
      .createResource$<TResponseDto>(this.$formGroup(), formGroup => this.createResourceFn$(formGroup))
      .subscribe({
        next: (res: TResponseDto) => {
          this.toasterService.showInfo('Saved');
          const redirectUrl = this.redirectUrlFn(res);
          this.navigationService.navigate(redirectUrl, { queryParams: { success: true } });
        },
        complete: () => {
          this.$formLoading.set(false);
        },
      });
  }
}
